/**
 * The access app (`release-access`): which steps each account-access flow
 * has, how a stepper reads, what a reset link requires, how an error reads
 * with its next actions, and where a signed-in tab lands. Pure functions; the
 * server enforces every rule.
 */

import type { LoginMfaMethod, LoginSuccessBody, ResetVerificationResponse } from './domain';
import { base64UrlToBytes } from './base64url';
import { resetIdentityCheckMessage } from './recovery';
import { contactPayloadValue, validateContactValue } from './validation';
import type { LoginMfaPending } from './login';

// ── Steps and the stepper ───────────────────────────────────────────────────

export interface AccessStep {
  key: string;
  /** Flow steps this one also covers (asking for the link and opening it are one step to the person). */
  covers?: readonly string[];
  label: string;
  /** One sentence on what happens in this step. */
  detail: string;
}

export type AccessStepState = 'done' | 'current' | 'upcoming';

export interface StepperView {
  current: number;
  total: number;
  /** "Step 2 of 3 · Confirm it’s you" */
  heading: string;
  items: Array<AccessStep & { state: AccessStepState; number: number }>;
  /** Labels still ahead of the current step. */
  remaining: string[];
  /** Share of steps already finished, 0-100: what the progress bar fills to. */
  percentComplete: number;
  /** Steps after the current one. */
  stepsRemaining: number;
  /** "Almost done" on the final step only, else ''. */
  cue: string;
}

/**
 * Progress for a step-based flow: how full the bar is and the cue near the
 * end. Mirrored by `@dealr/core-identity`'s `identityStepper`, which cannot
 * import this package; keep the two in step.
 */
export function stepProgress(index: number, total: number): Pick<StepperView, 'percentComplete' | 'stepsRemaining' | 'cue'> {
  const safeTotal = Math.max(1, total);
  const stepsRemaining = Math.max(0, safeTotal - index - 1);
  return {
    percentComplete: Math.round((Math.min(index, safeTotal) / safeTotal) * 100),
    stepsRemaining,
    cue: safeTotal > 1 && stepsRemaining === 0 ? 'Almost done' : '',
  };
}

const FIND: AccessStep = {
  key: 'request',
  covers: ['link'],
  label: 'Find your account',
  detail: 'Enter your username and open the link we email you. It works for 30 minutes, on any device.',
};
const VERIFY: AccessStep = { key: 'verify', label: 'Confirm it’s you', detail: 'Use your phone, a backup code, or a photo of your ID.' };
const PASSWORD: AccessStep = { key: 'password', label: 'Choose a new password', detail: 'Pick a strong password you have not used here before.' };

/**
 * Forgot password: find the account (ask for the link, open it), confirm it's
 * you, choose a password. `verify` drops out once the link needs nothing more.
 */
export function forgotPasswordSteps(requirement?: ResetRequirement['kind'] | null): AccessStep[] {
  return requirement === 'none' ? [FIND, PASSWORD] : [FIND, VERIFY, PASSWORD];
}

const WELCOME: AccessStep = { key: 'welcome', label: 'Welcome', detail: 'Your account is ready to activate.' };
const CHOOSE: AccessStep = { key: 'password', label: 'Choose a password', detail: 'Pick a strong password for your new account.' };
const ACTIVATION_ENROLL: AccessStep = { key: 'enroll', label: 'Set up two-step verification', detail: 'Add a second sign-in method before you start.' };

/**
 * Account activation (invite links). `verify` only when the server asks for a
 * proof (the user already enrolled a factor); `enroll` once the signed-in
 * session says `mfa_enrollment_required`.
 */
export function activationSteps(opts: { verify: boolean; enroll: boolean }): AccessStep[] {
  return [WELCOME, ...(opts.verify ? [VERIFY] : []), CHOOSE, ...(opts.enroll ? [ACTIVATION_ENROLL] : [])];
}

/**
 * The welcome line: "Pat Lee invited you to Sunrise Motors" when the server
 * names both, a partial line with one, generic otherwise.
 */
export function inviteWelcome(data: ResetVerificationResponse | null | undefined, product: string): string {
  const invite = data && data.invite ? data.invite : null;
  const who = invite && typeof invite.invited_by_name === 'string' ? invite.invited_by_name.trim() : '';
  const where = invite && typeof invite.dealership_name === 'string' ? invite.dealership_name.trim() : '';
  if (who && where) return `${who} invited you to ${where}.`;
  if (where) return `You’ve been invited to ${where}.`;
  if (who) return `${who} invited you to ${product}.`;
  return `You’ve been invited to ${product}.`;
}

/** `/changePass/verification` marks an invite link `activation: true`. */
export function isActivationLink(data: ResetVerificationResponse | null | undefined): boolean {
  return !!data && data.activation === true;
}

const CREDENTIALS: AccessStep = { key: 'credentials', label: 'Sign in', detail: 'Enter your username and password.' };
const SECOND: AccessStep = { key: 'second-factor', label: 'Confirm it’s you', detail: 'Use your phone or another sign-in method.' };
const ENROLL: AccessStep = { key: 'enroll', label: 'Set up two-step verification', detail: 'Add a second factor before you continue.' };

export function signInSteps(opts: { secondFactor: boolean; enrollment: boolean }): AccessStep[] {
  return [CREDENTIALS, ...(opts.secondFactor ? [SECOND] : []), ...(opts.enrollment ? [ENROLL] : [])];
}

export const FREEZE_STEPS: AccessStep[] = [
  { key: 'confirm', label: 'Confirm the freeze', detail: 'Lock the account and sign everyone out of it.' },
  { key: 'result', label: 'Next steps', detail: 'What happened and what to do now.' },
];

export function stepperView(steps: readonly AccessStep[], currentKey: string): StepperView {
  const index = Math.max(0, steps.findIndex((step) => step.key === currentKey || (step.covers ?? []).indexOf(currentKey) !== -1));
  const current = steps[index];
  return {
    current: index + 1,
    total: steps.length,
    heading: current ? `Step ${index + 1} of ${steps.length} · ${current.label}` : '',
    items: steps.map((step, i) => ({ ...step, number: i + 1, state: i < index ? 'done' : i === index ? 'current' : 'upcoming' })),
    remaining: steps.slice(index + 1).map((step) => step.label),
    ...stepProgress(index, steps.length),
  };
}

/** The flow finished: every step done, the bar full, and a heading that says so. */
export function completedStepperView(view: StepperView): StepperView {
  return {
    ...view,
    current: view.total,
    heading: `All ${view.total} steps done`,
    items: view.items.map((item) => ({ ...item, state: 'done' as const })),
    remaining: [],
    percentComplete: 100,
    stepsRemaining: 0,
    cue: '',
  };
}

// ── Forgot username ────────────────────────────────────────────────────────

/** Never says whether an account matched. `product` is "Dealr" or "Dealer Title Solutions". */
export function forgotUsernameSentMessage(product: string): string {
  return `If that email or phone number is on a ${product} account, we’ve sent the username to it. It can take a couple of minutes.`;
}

/**
 * The forgot-username form's one field: an email when it has an "@", else a
 * phone number. The body to send, or what to fix.
 */
export function forgotUsernameRequest(raw: string): { body: { email: string } | { phone: string }; channel: 'email' | 'phone' } | { error: string } {
  const value = (raw || '').trim();
  if (!value) return { error: 'Enter the email or cell phone number on your account.' };
  const field = value.indexOf('@') !== -1 ? 'email' : 'phone';
  const invalid = validateContactValue(field, value);
  if (invalid) return { error: invalid };
  const sent = contactPayloadValue(field, value);
  return field === 'email' ? { body: { email: sent }, channel: 'email' } : { body: { phone: sent }, channel: 'phone' };
}

// ── Tokens ──────────────────────────────────────────────────────────────────

/** Reset (80 alphanumerics), freeze (64 hex) and identity-check link tokens. Null when it can't be one. */
export function normalizeAccessToken(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const token = raw.trim();
  return /^[A-Za-z0-9_-]{16,256}$/.test(token) ? token : null;
}

// ── Reset link requirement ─────────────────────────────────────────────────

export type ResetRequirement =
  | { kind: 'none' }
  | { kind: 'mfa'; pending: LoginMfaPending }
  | { kind: 'identity_check'; status: string; captureToken: string | null }
  | { kind: 'contact_admin'; message: string };

/** The last resort, only when no self-service path exists for this account. */
export const CONTACT_ADMIN_MESSAGE =
  'Your account doesn’t have a way to finish this online yet. Your dealership admin can reset your access in a few minutes once they confirm it’s you.';

/**
 * The verification response as one of the states the page shows. A 404 (the
 * verification step is off) is `none`: the link alone is enough. Anything this
 * page cannot complete (an unknown requirement)
 * routes to the dealership admin rather than to a form.
 */
export function resetRequirement(status: number, data: ResetVerificationResponse | null | undefined): ResetRequirement {
  if (status === 404) return { kind: 'none' };
  const body = data || {};
  switch (body.requirement) {
    case 'none':
    case undefined:
      return { kind: 'none' };
    case 'mfa': {
      const methods: LoginMfaMethod[] = Array.isArray(body.methods) ? body.methods : [];
      if (!methods.length) return { kind: 'contact_admin', message: CONTACT_ADMIN_MESSAGE };
      const pending: LoginMfaPending = { mfaToken: '', methods };
      if (body.method_strength) pending.methodStrength = body.method_strength;
      if (Array.isArray(body.recovery_options)) pending.recoveryOptions = body.recovery_options;
      return { kind: 'mfa', pending };
    }
    case 'identity_check':
      return { kind: 'identity_check', status: body.identity_check || 'not_started', captureToken: body.capture_token || null };
    case 'contact_admin':
      return { kind: 'contact_admin', message: body.error || CONTACT_ADMIN_MESSAGE };
    default:
      return { kind: 'contact_admin', message: CONTACT_ADMIN_MESSAGE };
  }
}

export interface IdentityProgress {
  message: string;
  passed: boolean;
  /** Terminal: no retry on this link. */
  failed: boolean;
  /** Waiting on the person to finish the check elsewhere. */
  waiting: boolean;
}

export function identityProgress(status: string | null | undefined): IdentityProgress {
  const failed = status === 'failed' || status === 'declined' || status === 'expired';
  return {
    message: resetIdentityCheckMessage(status),
    passed: status === 'passed',
    failed,
    waiting: status === 'pending',
  };
}

// ── Errors and next actions ────────────────────────────────────────────────

export type AccessNextAction = 'retry' | 'request-link' | 'reset-password' | 'sign-in' | 'contact-admin' | 'wait';

export interface AccessErrorView {
  kind: 'lockout' | 'expired-link' | 'reset-required' | 'blocked' | 'network' | 'invalid' | 'generic';
  title: string;
  message: string;
  actions: AccessNextAction[];
}

/** The password-migration 403 (`password_reset_required`): the server has already emailed a reset link. */
export const PASSWORD_RESET_REQUIRED_MESSAGE = 'For your security, you need to choose a new password. We emailed you a reset link, or you can request one now.';

/** `POST /api/login` refused the password because the account must reset it first (main.js shows the same message). */
export function isPasswordResetRequired(status: number, data: unknown): boolean {
  return status === 403 && !!data && typeof data === 'object' && (data as { password_reset_required?: unknown }).password_reset_required === true;
}

/**
 * How a failed call reads. `context` says whether the token was the subject
 * of the call, so a 404 means a dead link rather than a missing page.
 */
export function accessErrorView(status: number, serverMessage: string, context: 'link' | 'form' | 'reset-required'): AccessErrorView {
  const message = serverMessage && serverMessage.trim() ? serverMessage.trim() : '';
  if (status === 429 || status === 423) {
    return { kind: 'lockout', title: 'Too many attempts', message: message || 'For your security this is paused. Wait a few minutes, then try again.', actions: ['wait', 'sign-in'] };
  }
  if (status === 0) {
    return { kind: 'network', title: 'You appear to be offline', message: 'Check your connection and try again.', actions: ['retry'] };
  }
  if (context === 'link' && (status === 401 || status === 404 || status === 410)) {
    return { kind: 'expired-link', title: 'This link has expired or was already used', message: message || 'Links work once and for a limited time. Request a new one.', actions: ['request-link'] };
  }
  if (status === 403 && context === 'reset-required') {
    return { kind: 'reset-required', title: 'Time to choose a new password', message: message || PASSWORD_RESET_REQUIRED_MESSAGE, actions: ['reset-password'] };
  }
  if (status === 403) {
    return { kind: 'blocked', title: 'This can’t be done here', message: message || CONTACT_ADMIN_MESSAGE, actions: ['sign-in', 'contact-admin'] };
  }
  if (status === 400 || status === 401 || status === 422) {
    return { kind: 'invalid', title: 'That didn’t work', message: message || 'Check what you entered and try again.', actions: ['retry'] };
  }
  return { kind: 'generic', title: 'Something went wrong', message: message || 'Try again in a moment.', actions: ['retry'] };
}

// ── Where a signed-in tab lands ────────────────────────────────────────────

/** The `uid` claim of the session JWT, or null. Only reads the payload; the server verifies. */
export function sessionUserId(token: string | null | undefined): number | null {
  const part = typeof token === 'string' ? token.split('.')[1] : undefined;
  if (!part) return null;
  try {
    const bytes = base64UrlToBytes(part);
    let json = '';
    for (let i = 0; i < bytes.length; i++) json += String.fromCharCode(bytes[i] ?? 0);
    const uid = Number((JSON.parse(json) as Record<string, unknown>)['uid']);
    return Number.isInteger(uid) && uid > 0 ? uid : null;
  } catch (e) {
    return null;
  }
}

/** Only a root-relative path, or an absolute URL on `origin`. */
export function isSameOriginTarget(url: string, origin: string): boolean {
  if (url.indexOf('/') === 0) return url.indexOf('//') !== 0 && url.indexOf('/\\') !== 0;
  const match = /^(https?:\/\/[^/?#]+)(?:[/?#]|$)/i.exec(url);
  return !!match && match[1]!.toLowerCase() === origin.toLowerCase();
}

/** Re-points a captured redirect at the user who just signed in (mirrors `main.js` `applyUserPrefix`). */
export function applyUserPrefix(path: string, userId: number): string {
  if (/^\/u\/[0-9]+\//.test(path)) return path.replace(/^\/u\/[0-9]+\//, `/u/${userId}/`);
  if (path.indexOf('/') === 0) return `/u/${userId}${path}`;
  if (path.indexOf('/u/') > 0) return path.replace(/\/u\/[0-9]+\//, `/u/${userId}/`);
  return path;
}

/**
 * Where to send the tab after a successful sign-in or reset, mirroring the
 * login page: enrollment first, then a same-origin `redirUrl` (never into
 * /admin for a dealer user), else the user's home.
 */
export function loginDestination(body: LoginSuccessBody, opts: { redirUrl?: string | null; origin: string }): string | null {
  const uid = sessionUserId(body.token);
  if (uid === null) return null;
  const type = typeof body.type === 'string' ? body.type : undefined;
  if (body.mfa_enrollment_required === true && type !== 'admin') return `/u/${uid}/dealer/user/settings`;
  // account-security: past the grace period only settings (where the check runs) is usable (main.js goToLoginTarget).
  if (body['identity_verification_blocked'] === true && type !== 'admin') return `/u/${uid}/dealer/user/settings`;
  const fallback = `/u/${uid}${type === 'admin' ? '/admin/' : '/dealer/'}`;
  const redir = opts.redirUrl;
  if (!redir || !isSameOriginTarget(redir, opts.origin)) return fallback;
  if (type !== undefined && type !== 'admin' && redir.indexOf('/admin') !== -1) return fallback;
  return applyUserPrefix(redir, uid);
}
